"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.FunnelPlot = FunnelPlot;
var _objectWithoutPropertiesLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutPropertiesLoose"));
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var React = _react;
var _propTypes = _interopRequireDefault(require("prop-types"));
var _d3Shape = require("@mui/x-charts-vendor/d3-shape");
var _internals = require("@mui/x-charts/internals");
var _hooks = require("@mui/x-charts/hooks");
var _styles = require("@mui/material/styles");
var _FunnelSection = require("./FunnelSection");
var _labelUtils = require("./labelUtils");
var _useFunnelSeries = require("../hooks/useFunnelSeries");
var _curves = require("./curves");
var _jsxRuntime = require("react/jsx-runtime");
const _excluded = ["onItemClick", "gap"];
_internals.cartesianSeriesTypes.addType('funnel');
const useAggregatedData = gap => {
  const seriesData = (0, _useFunnelSeries.useFunnelSeriesContext)();
  const {
    xAxis,
    xAxisIds
  } = (0, _hooks.useXAxes)();
  const {
    yAxis,
    yAxisIds
  } = (0, _hooks.useYAxes)();
  const allData = React.useMemo(() => {
    if (seriesData === undefined) {
      return [];
    }
    const {
      series,
      seriesOrder
    } = seriesData;
    const defaultXAxisId = xAxisIds[0];
    const defaultYAxisId = yAxisIds[0];
    const isHorizontal = Object.values(series).some(s => s.layout === 'horizontal');
    const result = seriesOrder.map(seriesId => {
      const currentSeries = series[seriesId];
      const xAxisId = currentSeries.xAxisId ?? defaultXAxisId;
      const yAxisId = currentSeries.yAxisId ?? defaultYAxisId;
      const valueFormatter = currentSeries.valueFormatter;
      const baseScaleConfig = isHorizontal ? xAxis[xAxisId] : yAxis[yAxisId];
      const isXAxisBand = xAxis[xAxisId].scaleType === 'band';
      const isYAxisBand = yAxis[yAxisId].scaleType === 'band';
      const bandWidth = (isXAxisBand || isYAxisBand) && baseScaleConfig.scale?.bandwidth() || 0;
      const xScale = xAxis[xAxisId].scale;
      const yScale = yAxis[yAxisId].scale;
      const xPosition = (value, bandIndex, stackOffset, useBand) => {
        if (isXAxisBand) {
          const position = xScale(bandIndex);
          return useBand ? position + bandWidth : position;
        }
        return xScale(isHorizontal ? value + (stackOffset || 0) : value);
      };
      const yPosition = (value, bandIndex, stackOffset, useBand) => {
        if (isYAxisBand) {
          const position = yScale(bandIndex);
          return useBand ? position + bandWidth : position;
        }
        return yScale(isHorizontal ? value : value + (stackOffset || 0));
      };
      const allY = currentSeries.dataPoints.flatMap((d, dataIndex) => d.flatMap(v => yPosition(v.y, baseScaleConfig.data?.[dataIndex], v.stackOffset, v.useBandWidth)));
      const allX = currentSeries.dataPoints.flatMap((d, dataIndex) => d.flatMap(v => xPosition(v.x, baseScaleConfig.data?.[dataIndex], v.stackOffset, v.useBandWidth)));
      const minPoint = {
        x: Math.min(...allX),
        y: Math.min(...allY)
      };
      const maxPoint = {
        x: Math.max(...allX),
        y: Math.max(...allY)
      };
      return currentSeries.dataPoints.flatMap((values, dataIndex) => {
        const color = currentSeries.data[dataIndex].color;
        const id = `${seriesId}-${dataIndex}`;
        const sectionLabel = typeof currentSeries.sectionLabel === 'function' ? currentSeries.sectionLabel({
          dataIndex,
          seriesId,
          value: currentSeries.data[dataIndex].value
        }) : currentSeries.sectionLabel;
        const isIncreasing = currentSeries.dataDirection === 'increasing';
        const curve = (0, _curves.getFunnelCurve)(currentSeries.curve, {
          isHorizontal,
          gap,
          position: dataIndex,
          sections: currentSeries.dataPoints.length,
          borderRadius: currentSeries.borderRadius,
          isIncreasing,
          min: minPoint,
          max: maxPoint
        });
        const line = (0, _d3Shape.line)().x(d => xPosition(d.x, baseScaleConfig.data?.[dataIndex], d.stackOffset, d.useBandWidth)).y(d => yPosition(d.y, baseScaleConfig.data?.[dataIndex], d.stackOffset, d.useBandWidth)).curve(curve);
        return {
          d: line(values),
          color,
          id,
          seriesId,
          dataIndex,
          variant: currentSeries.variant,
          label: sectionLabel !== false && (0, _extends2.default)({}, (0, _labelUtils.positionLabel)((0, _extends2.default)({}, sectionLabel, {
            xPosition,
            yPosition,
            isHorizontal,
            values,
            dataIndex,
            baseScaleData: baseScaleConfig.data ?? []
          })), (0, _labelUtils.alignLabel)(sectionLabel ?? {}), {
            value: valueFormatter ? valueFormatter(currentSeries.data[dataIndex], {
              dataIndex
            }) : currentSeries.data[dataIndex].value?.toLocaleString()
          })
        };
      });
    });
    return result.flat();
  }, [seriesData, xAxis, xAxisIds, yAxis, yAxisIds, gap]);
  return allData;
};
function FunnelPlot(props) {
  const {
      onItemClick,
      gap
    } = props,
    other = (0, _objectWithoutPropertiesLoose2.default)(props, _excluded);
  const theme = (0, _styles.useTheme)();
  const data = useAggregatedData(gap);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(React.Fragment, {
    children: [data.map(({
      d,
      color,
      id,
      seriesId,
      dataIndex,
      variant
    }) => /*#__PURE__*/(0, _react.createElement)(_FunnelSection.FunnelSection, (0, _extends2.default)({}, other, {
      d: d,
      color: color,
      key: id,
      dataIndex: dataIndex,
      seriesId: seriesId,
      variant: variant,
      onClick: onItemClick && (event => {
        onItemClick(event, {
          type: 'funnel',
          seriesId,
          dataIndex
        });
      })
    }))), data.map(({
      id,
      label
    }) => {
      if (!label) {
        return null;
      }
      return /*#__PURE__*/(0, _jsxRuntime.jsx)("text", {
        x: label.x,
        y: label.y,
        textAnchor: label.textAnchor,
        dominantBaseline: label.dominantBaseline,
        stroke: "none",
        pointerEvents: "none",
        fontFamily: theme.typography.body2.fontFamily,
        fontSize: theme.typography.body2.fontSize,
        fontSizeAdjust: theme.typography.body2.fontSizeAdjust,
        fontWeight: theme.typography.body2.fontWeight,
        letterSpacing: theme.typography.body2.letterSpacing,
        fontStretch: theme.typography.body2.fontStretch,
        fontStyle: theme.typography.body2.fontStyle,
        fontVariant: theme.typography.body2.fontVariant,
        fill: (theme.vars || theme)?.palette?.text?.primary,
        children: label.value
      }, id);
    })]
  });
}
process.env.NODE_ENV !== "production" ? FunnelPlot.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * The gap, in pixels, between funnel sections.
   * @default 0
   */
  gap: _propTypes.default.number,
  /**
   * Callback fired when a funnel item is clicked.
   * @param {React.MouseEvent<SVGElement, MouseEvent>} event The event source of the callback.
   * @param {FunnelItemIdentifier} funnelItemIdentifier The funnel item identifier.
   */
  onItemClick: _propTypes.default.func,
  /**
   * The props used for each component slot.
   * @default {}
   */
  slotProps: _propTypes.default.object,
  /**
   * Overridable component slots.
   * @default {}
   */
  slots: _propTypes.default.object
} : void 0;